import { X, Copy, Check, Share2, Gift, BarChart3 } from "lucide-react";
import { useState } from "react";
import axios from "axios";
import { API, POODLE_CHARACTERS } from "./constants";
import { getDeviceId } from "./deviceId";
import { isHotelModeActive, getCachedHotel } from "./poodletalk/hotelClient";

const ParentsCorner = ({ isOpen, onClose, onCreditsUpdate, onOpenUsageReport }) => {
  const [copied, setCopied] = useState(false);
  const [redeemCode, setRedeemCode] = useState('');
  const [redeemMessage, setRedeemMessage] = useState('');
  const [redeemSuccess, setRedeemSuccess] = useState(false);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const appUrl = window.location.href;
  
  const handleCopyLink = () => {
    navigator.clipboard.writeText(appUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Poodle Talk - A Safe Space for Kids',
          text: 'A friendly chat app where kids can talk to poodle friends about their feelings.',
          url: appUrl,
        });
      } catch (e) {}
    } else {
      handleCopyLink();
    }
  };

  if (!isOpen) return null;

  const handleRedeem = async (e) => {
    e.preventDefault();
    if (!redeemCode.trim() || isRedeeming) return;
    setIsRedeeming(true);
    setRedeemMessage('');
    try {
      const res = await axios.post(`${API}/redeem`, { code: redeemCode.trim(), device_id: getDeviceId() });
      if (res.data.success) {
        setRedeemSuccess(true);
        setRedeemMessage(res.data.message);
        if (onCreditsUpdate) onCreditsUpdate(res.data.paid_remaining);
        setRedeemCode('');
      } else {
        setRedeemSuccess(false);
        setRedeemMessage(res.data.message);
      }
    } catch (error) {
      setRedeemSuccess(false);
      setRedeemMessage('Something went wrong. Please try again.');
    } finally {
      setIsRedeeming(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="parents-corner-modal" onClick={onClose}>
      <div className="bg-[#FDFBF7] rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button 
          onClick={onClose}
          data-testid="parents-corner-close-button"
          className="absolute top-4 right-4 text-[#6B6B6B] hover:text-[#4A4A4A] transition-colors"
        >
          <X className="w-7 h-7" />
        </button>
        
        <h2 className="text-2xl font-bold text-[#4A4A4A] mb-4">Parents / Grown Up Corner</h2>
        
        <div className="space-y-4 text-[#4A4A4A]">
          <div className="bg-[#FFF7D6] rounded-2xl p-4">
            <h3 className="font-bold text-lg mb-2">About Poodle Talk</h3>
            <p className="text-sm leading-relaxed">
              Poodle Talk is part of <strong>Poodles & Friends</strong> — a family-friendly educational music and media project. 
              Our gentle poodle characters provide a calm, safe space for children to express their feelings.
            </p>
          </div>
          
          <div className="bg-[#E8F5E9] rounded-2xl p-4">
            <h3 className="font-bold text-lg mb-2">Children's Safety</h3>
            <p className="text-sm leading-relaxed">
              • We do <strong>not</strong> collect any personal information from children<br/>
              • <strong>No login is required</strong> to use the app or the free trial<br/>
              • Optional Google sign-in is available <strong>for parents only</strong>, so chat credits travel between WiFi and mobile data<br/>
              • Conversations are not stored<br/>
              • AI responses are designed to be gentle and age-appropriate<br/>
              • Emergency guidance directs children to trusted adults
            </p>
          </div>

          <div className="bg-[#F9F6EE] rounded-2xl p-4">
            <h3 className="font-bold text-lg mb-2">Privacy & Technical Info</h3>
            <p className="text-sm leading-relaxed">
              • <strong>No cookies</strong> are used for tracking or advertising<br/>
              • <strong>No third-party trackers</strong> (no Google Analytics, Facebook, Mixpanel, etc.)<br/>
              • We keep simple, anonymous engagement counters to help us improve the app — for example, how often a picture card category is opened, which emotion buttons are tapped most, and which age mode (Tiny / Kids / Teens) is most used. <strong>These are counts only — no chat content, no child names, no personal data</strong>. This helps us design better support for children, especially those who communicate through picture cards rather than typing<br/>
              • The only data stored on our server is what's strictly needed to run the service: the parent's email at purchase, and (if a parent chooses to sign in) their Google name + email so credits follow them across networks<br/>
              • Mood check-ins are saved <strong>only on your device</strong> (localStorage) and never transmitted<br/>
              • Voice features use your device's built-in speech engine — no audio is recorded or uploaded<br/>
              • AI chat is processed in real-time by <strong>Claude Sonnet 4.5</strong> (Anthropic). Per Anthropic's <a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="underline text-[#A55B64]">API privacy policy</a>, messages are <strong>not used to train AI models</strong>. <strong>Chat content is not saved</strong> on our servers after the session ends<br/>
              • When you receive emails from us, we use basic tracking (powered by Resend) to see if the email was opened and which links were clicked. This helps us understand which schools and parents engaged with our outreach. <strong>No personal data is sold or shared with third parties.</strong> You can unsubscribe at any time using the link at the bottom of any email.<br/>
              • This app is fully compliant with children's online safety standards
            </p>
          </div>

          <div className="bg-gradient-to-br from-[#F9F6EE] to-[#FFE5C2] rounded-2xl p-4 border border-[#E0D4B8]" data-testid="privacy-and-safety-section">
            <h3 className="font-bold text-lg mb-2">🛡️ Privacy &amp; Safety Overview</h3>
            <p className="text-sm leading-relaxed mb-3">
              We have written a calm, plain-English summary of how Poodle Talk handles privacy, data, AI processing, and child safety — for parents, teachers, nurseries, schools, charities, and safeguarding leads.
            </p>
            <a
              href="/privacy"
              data-testid="privacy-policy-link"
              className="inline-block bg-[#A55B64] hover:bg-[#8A4951] text-white px-4 py-2 rounded-full text-sm font-medium transition-colors mr-2 mb-2"
            >
              View Privacy &amp; Safety →
            </a>
            <a
              href="/privacy_and_safety_overview.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="privacy-pdf-link"
              className="inline-block bg-white hover:bg-[#F3F0E6] text-[#4A4A4A] px-4 py-2 rounded-full text-sm font-medium transition-colors border border-[#E0D4B8] mb-2"
            >
              📄 Download PDF
            </a>
            <p className="text-xs text-[#6B6B6B] mt-3 leading-relaxed">
              <strong>Children's privacy (UK GDPR / EU GDPR / COPPA):</strong> Poodle Talk is designed around the UK Children's Code (Age-Appropriate Design Code) first. We do not knowingly collect personal information from children under 13. Sign-in and purchasing are designed for parents and supervising adults only.
            </p>
            <p className="text-xs text-[#6B6B6B] mt-2 leading-relaxed" data-testid="data-request-line">
              <strong>Your data, your rights:</strong> To access, correct, or delete the personal data we hold about you, email <a href="mailto:hello@poodlesandfriends.uk?subject=DATA%20REQUEST" className="underline text-[#A55B64]">hello@poodlesandfriends.uk</a> with subject <em>DATA REQUEST</em>. We respond within 30 days. No cost.
            </p>
            <p className="text-xs text-[#6B6B6B] mt-2 leading-relaxed" data-testid="ico-line">
              <strong>ICO registered:</strong> Registered with the Information Commissioner&apos;s Office (ICO). We process personal data in accordance with UK data protection legislation.
            </p>
            <a
              href="https://www.poodlesandfriends.uk/policy-info"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="full-policy-link-parents-corner"
              className="inline-block mt-3 bg-white hover:bg-[#F3F0E6] text-[#4A4A4A] px-4 py-2 rounded-full text-sm font-medium transition-colors border border-[#E0D4B8]"
            >
              📋 Full Policy (all Poodles &amp; Friends projects)
            </a>
          </div>

          <div className="bg-gradient-to-br from-[#FFE5C2] to-[#FCEAEA] rounded-2xl p-4 border border-[#E0D4B8]">
            <h3 className="font-bold text-lg mb-2">📚 Learn more about Poodle Talk</h3>
            <p className="text-sm leading-relaxed mb-3">
              Want a deeper look at how Poodle Talk supports children's learning and wellbeing, plus our independent AI safeguarding evidence? Visit our overview page on the Poodles &amp; Friends website to view:
            </p>
            <ul className="text-sm leading-relaxed list-disc list-inside mb-3 ml-1">
              <li>Learning &amp; Wellbeing Overview</li>
              <li>Poodle Talk AI Safeguarding Stress Test Report</li>
            </ul>
            <a
              href="https://www.poodlesandfriends.uk/poodle-talk-learning-and-wellbeing-overview"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="parents-corner-overview-link"
              className="inline-block bg-[#A55B64] hover:bg-[#8A4951] text-white px-4 py-2 rounded-full text-sm font-medium transition-colors"
            >
              View the Overview →
            </a>
          </div>
          
          <div className="bg-[#E6F0F9] rounded-2xl p-4">
            <h3 className="font-bold text-lg mb-2">Parental Guidance</h3>
            <p className="text-sm leading-relaxed">
              While our poodle friends are kind and supportive, they are <strong>not a replacement</strong> for real conversations 
              with parents, teachers, or counselors. We encourage supervised use and open discussions about feelings.
            </p>
          </div>

          <div className="bg-[#FFF7D6] rounded-2xl p-4">
            <h3 className="font-bold text-lg mb-2">How It Works</h3>
            <p className="text-sm leading-relaxed">
              • Conversations are powered by <strong>Claude AI by Anthropic</strong> — one of the safest AI models available<br/>
              • Every response follows strict <strong>child-safety rules</strong> built into the system<br/>
              • The AI will <strong>never</strong> give medical, legal, or professional advice<br/>
              • If a child mentions danger or distress, the AI provides <strong>emergency contacts (UK first)</strong>:<br/>
              &nbsp;&nbsp;&nbsp;— <strong>UK:</strong> 999 (emergency), <strong>Childline 0800 1111</strong> (free, 24/7), <strong>Samaritans 116 123</strong> (free, 24/7)<br/>
              &nbsp;&nbsp;&nbsp;— <strong>US/Canada:</strong> 911 (emergency)<br/>
              &nbsp;&nbsp;&nbsp;— <strong>Other countries:</strong> Local emergency services, or <a href="https://findahelpline.com" target="_blank" rel="noopener noreferrer" className="underline text-[#A55B64]">findahelpline.com</a><br/>
              • <strong>Teen mode</strong> provides age-appropriate crisis support:<br/>
              &nbsp;&nbsp;&nbsp;— <strong>UK:</strong> Text SHOUT to <strong>85258</strong> (free, 24/7)<br/>
              &nbsp;&nbsp;&nbsp;— <strong>US/Canada:</strong> Text HOME to <strong>741741</strong> (free, 24/7)<br/>
              • All responses are generated in real-time and <strong>never stored or reviewed</strong>
            </p>
          </div>

          <div className="bg-[#F3E8FF] rounded-2xl p-4">
            <h3 className="font-bold text-lg mb-2">For Schools & SEND Support</h3>
            <p className="text-sm leading-relaxed mb-3">
              Poodle Talk can support children with special educational needs as a gentle companion tool. It is designed to help children express feelings and communicate in a calm, simple way, especially for those who may find words difficult.
            </p>
            <p className="text-sm leading-relaxed mb-3">
              It includes visual emotion faces, picture cards inspired by AAC approaches, calm down timers, and simple auto-read features to support accessibility.
            </p>
            <p className="text-sm leading-relaxed mb-3">
              The focus is on creating a safe, low-pressure space where children can interact at their own pace, both at home and in school settings.
            </p>
            <div className="bg-white/60 rounded-xl p-3">
              <p className="text-sm leading-relaxed text-[#4A4A4A]">
                <strong>Picture Cards are always <span className="text-[#5A8A5A]">FREE</span> to use</strong>
                <br/><br/>
                We know that many children with SEND communicate primarily through visual tools. That's why tapping any picture card, emotion button, or quick message card is designed to feel open, easy, and accessible.
                <br/><br/>
                Your child can explore, tap, and interact <strong className="text-[#5A8A5A]">freely</strong>, with our poodle friends gently responding in a way that feels natural and supportive.
                <br/><br/>
                Chat credits are only used when a custom message is typed or spoken, allowing deeper conversations, storytelling, and personalised responses when needed.
                <br/><br/>
                This means children who rely on visual communication can enjoy Poodle Talk comfortably, while still having the option to explore more advanced interactions when they're ready.
              </p>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-[#FCEAEA] to-[#FFF7D6] rounded-2xl p-4 border border-[#F3C4C4]" data-testid="comfort-cards-gift-section">
            <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
              <Gift className="w-5 h-5 text-[#A55B64]" />
              <span>Little Friend Comfort Cards</span>
            </h3>
            <p className="text-sm leading-relaxed mb-3">
              A gentle, screen-free gift idea: each poodle has its own physical portrait card you can keep at your child's bedside, in a school bag, or on the fridge. A soft reminder that their poodle friend is always nearby. 💛
            </p>
            <div className="grid grid-cols-2 gap-2">
              {POODLE_CHARACTERS.map((p) => (
                <a
                  key={p.id}
                  href={p.cardUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`comfort-card-link-${p.id}`}
                  className="flex items-center gap-2 bg-white/80 hover:bg-white rounded-xl px-3 py-2 transition-colors border border-[#F3C4C4]/60"
                >
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#4A4A4A] truncate">{p.name}'s card</div>
                    <div className="text-[10px] text-[#9A9A9A] truncate">Shop card →</div>
                  </div>
                </a>
              ))}
            </div>
            <p className="text-[11px] text-[#9A9A9A] mt-3">
              Opens the Poodles &amp; Friends shop in a new tab.
            </p>
          </div>

          <div className="bg-[#FCEAEA] rounded-2xl p-4">
            <h3 className="font-bold text-lg mb-2">More from Poodles & Friends</h3>
            <p className="text-sm leading-relaxed mb-3">
              Discover our calming music, learning videos, and comfort cards designed to help children learn gently and grow with confidence.
            </p>
            <div className="flex flex-wrap gap-2">
              <a href="https://www.poodlesandfriends.uk/" target="_blank" rel="noopener noreferrer" 
                className="text-xs bg-white px-3 py-1.5 rounded-full text-[#4A7AA5] hover:bg-[#C9DFE9] transition-colors">
                Website
              </a>
              <a href="https://www.youtube.com/@PoodlesandFriends/videos" target="_blank" rel="noopener noreferrer"
                className="text-xs bg-white px-3 py-1.5 rounded-full text-[#C75050] hover:bg-[#FFCCCC] transition-colors">
                YouTube
              </a>
              <a href="https://open.spotify.com/artist/68s3EpmTCtB1lCOLoMvK7J" target="_blank" rel="noopener noreferrer"
                className="text-xs bg-white px-3 py-1.5 rounded-full text-[#1DB954] hover:bg-[#D0F0D0] transition-colors">
                Spotify
              </a>
              <a href="https://music.apple.com/us/artist/poodlesandfriends/1877386564" target="_blank" rel="noopener noreferrer"
                className="text-xs bg-white px-3 py-1.5 rounded-full text-[#FA243C] hover:bg-[#FFE0E0] transition-colors">
                Apple Music
              </a>
            </div>
          </div>

          <div className="bg-[#FFF0F0] rounded-2xl p-4 border border-[#F5D0D0]">
            <h3 className="font-bold text-lg mb-2">Support Us</h3>
            <p className="text-sm leading-relaxed mb-3">
              Poodle Talk is built with care by a <a href="https://www.poodlesandfriends.uk/behind-the-scenes" target="_blank" rel="noopener noreferrer" className="text-[#C75050] underline hover:text-[#A53030] font-medium">mother</a> who believes every child deserves a safe space to express themselves.
              <br/><br/>
              This app is completely free to use. It is currently supported personally, with the aim of keeping it accessible for as many children as possible.
              <br/><br/>
              If it has helped your child, you are warmly invited to support us so we can continue maintaining it, developing new features, and reaching more families — especially those with special educational needs.
            </p>
            <div className="bg-white/60 rounded-xl p-3 mb-3">
              <p className="text-xs leading-relaxed text-[#6B6B6B]">
                <strong>Why are there chat limits?</strong><br/>
                Poodle Talk uses advanced AI technology to provide thoughtful, supportive conversations for children. Each interaction carries a small running cost, so to keep the app accessible to as many families as possible, each device includes a limited number of free chat messages to try.<br/><br/>
                After this, a grown-up can choose to add more chats if needed. Children can always continue using Picture Cards, Avatar Maker, and Games — which remain free.<br/><br/>
                Your support helps us sustain these high-quality conversations and keep Poodle Talk available for more children.
              </p>
            </div>
            <a 
              href="https://www.poodlesandfriends.uk/support-poodles-and-friends-helping-children" 
              target="_blank" 
              rel="noopener noreferrer"
              data-testid="support-us-link"
              className="inline-block text-sm bg-[#C75050] text-white px-5 py-2 rounded-full hover:bg-[#A53030] transition-colors font-medium shadow-sm"
            >
              Support Poodles & Friends
            </a>
          </div>

          <div className="bg-[#F9F6EE] rounded-2xl p-4 border border-[#E8E5DC]">
            <h3 className="font-bold text-lg mb-2">Redeem a Chat Credit Code</h3>
            <p className="text-sm text-[#6B6B6B] mb-3">
              If you have purchased a chat credit code, enter it below to add more chat time for your child.
            </p>
            <form onSubmit={handleRedeem} className="flex gap-2">
              <input
                type="text"
                value={redeemCode}
                onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                placeholder="Enter your code here..."
                data-testid="parents-credit-code-input"
                disabled={isRedeeming}
                className="flex-1 px-4 py-2.5 bg-white border-2 border-[#E8E5DC] rounded-full text-sm text-[#4A4A4A] placeholder-[#C9B896] outline-none focus:border-[#C9B896] focus:ring-2 focus:ring-[#C9B896]/20 transition-all disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={isRedeeming || !redeemCode.trim()}
                data-testid="parents-credit-code-submit"
                className="px-5 py-2.5 bg-[#E8D4A8] text-[#5A4A32] rounded-full text-sm font-semibold hover:bg-[#D9C599] transition-all hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
              >
                {isRedeeming ? '...' : 'Redeem'}
              </button>
            </form>
            {redeemMessage && (
              <p className={`text-xs mt-2 text-center font-medium ${redeemSuccess ? 'text-[#5A8A5A]' : 'text-[#A55B64]'}`} data-testid="parents-redeem-message">
                {redeemMessage}
              </p>
            )}
            <div className="text-center mt-4">
              <a href="/buy" className="inline-block px-5 py-2.5 bg-[#E8D4A8] text-[#5A4A32] rounded-full text-sm font-semibold hover:bg-[#D9C599] transition-all hover:scale-105">
                Buy more chat credits
              </a>
            </div>
          </div>

          {/* Share Section with QR Code */}
          <div className="bg-[#F3F0E6] rounded-2xl p-4 text-center">
            <h3 className="font-bold text-lg mb-2">Share Poodle Talk</h3>
            <p className="text-sm text-[#6B6B6B] mb-3">Share with families, teachers, or friends</p>
            
            <div className="bg-white rounded-xl p-3 inline-block mb-3">
              <img 
                src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(appUrl)}`}
                alt="QR Code to share Poodle Talk"
                className="w-36 h-36"
                data-testid="share-qr-code"
              />
            </div>
            <p className="text-xs text-[#9A9A9A] mb-3">Scan with your phone camera</p>
            
            <div className="flex justify-center gap-2">
              <button 
                onClick={handleCopyLink}
                data-testid="copy-link-button"
                className="flex items-center gap-1.5 px-4 py-2 bg-white rounded-full text-xs font-medium text-[#4A4A4A] hover:bg-[#E8E5DC] transition-all"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? 'Copied!' : 'Copy Link'}
              </button>
              <button 
                onClick={handleShare}
                data-testid="share-button"
                className="flex items-center gap-1.5 px-4 py-2 bg-[#4A7AA5] rounded-full text-xs font-medium text-white hover:bg-[#3A6A95] transition-all"
              >
                <Share2 className="w-3.5 h-3.5" />
                Share
              </button>
            </div>
          </div>
          
          <div className="text-center pt-2">
            <p className="text-xs text-[#9A9A9A]">
              Contact: shop@poodlesandfriends.uk
            </p>
            <a href="https://www.poodlesandfriends.uk/policy-info" target="_blank" rel="noopener noreferrer"
              className="text-xs text-[#4A7AA5] hover:underline">
              View Full Policies
            </a>
          </div>

          {/* Organisation manager: view usage report (only shown on activated org devices) */}
          {isHotelModeActive() && (
            <div className="bg-white border-2 border-[#E8D4A8] rounded-2xl p-4" data-testid="parents-usage-report-block">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="w-4 h-4 text-[#B89823]" />
                <h3 className="font-bold text-sm text-[#4A4A4A]">Organisation manager</h3>
              </div>
              <p className="text-xs text-[#6B6B6B] leading-relaxed mb-3">
                {(() => {
                  const c = getCachedHotel();
                  return `See how ${c?.hotel_name || 'your organisation'} is using its chat credits — daily and weekly usage across every venue, code and device.`;
                })()}
              </p>
              <button
                onClick={() => { onClose && onClose(); onOpenUsageReport && onOpenUsageReport(); }}
                data-testid="parents-open-usage-report"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#E8D4A8] text-[#5A4A32] rounded-full font-semibold text-sm hover:bg-[#D9C599] transition-all"
              >
                <BarChart3 className="w-4 h-4" /> View usage report
              </button>
              <p className="text-[10px] text-[#9A9A9A] mt-2 text-center">
                Manager email + organisation access code required. Never shown on this device without signing in.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParentsCorner;
